var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useState, useCallback, useEffect, useRef } from "react";
import { useMutation } from "react-query";
import { useIntl } from "react-intl";
import { useOkapiKy } from "@folio/stripes/core";
import { isReportJobStarted, } from "../types/reports/reportJob";
import { useReportJob } from "./useReportJob";
import { toErrorWithResponse } from "../utils/httpErrors";
var HTTP_TOO_MANY_REQUESTS = 429;
/**
 * Shared hook for file-producing report forms.
 *
 * Every file report is a background job (HTTP 202 `{jobId}`), tracked by
 * {@link useReportJob}: the form shows „Generuje se…" while it runs and
 * „Stáhnout" once it is done; a failed job opens the error modal. A full report
 * queue (HTTP 429) asks the user to try again in a while.
 */
export var useReportMutation = function (_a) {
    var reportType = _a.reportType, generateReport = _a.generateReport, successMessageId = _a.successMessageId, errorMessageId = _a.errorMessageId;
    var formatMessage = useIntl().formatMessage;
    var formatMessageRef = useRef(formatMessage);
    formatMessageRef.current = formatMessage;
    var ky = useOkapiKy();
    var reportJob = useReportJob(reportType);
    var isDone = reportJob.isDone, isFailed = reportJob.isFailed, pollFailed = reportJob.pollFailed, resetJob = reportJob.reset;
    var _b = useState(false), errorModalOpen = _b[0], setErrorModalOpen = _b[1];
    var _c = useState(""), errorMessage = _c[0], setErrorMessage = _c[1];
    var _d = useState(""), successMessage = _d[0], setSuccessMessage = _d[1];
    var _e = useState(false), isDownloading = _e[0], setIsDownloading = _e[1];
    var showError = useCallback(function (messageId) {
        setSuccessMessage("");
        setErrorMessage(formatMessage({ id: messageId }));
        setErrorModalOpen(true);
    }, [formatMessage]);
    var clearSuccessMessage = useCallback(function () { return setSuccessMessage(""); }, []);
    var closeErrorModal = useCallback(function () {
        setErrorModalOpen(false);
        // a failed job is acknowledged — do not show it again when the user comes back
        if (isFailed)
            resetJob();
    }, [isFailed, resetJob]);
    useEffect(function () {
        // the file is ready, not downloaded yet — that is confirmed after „Stáhnout";
        // keyed on the job only, so a re-render never replaces the download confirmation
        if (isDone) {
            setSuccessMessage(formatMessageRef.current({
                id: "ui-br-agency.outputs.report-job.ready",
            }));
        }
    }, [isDone, reportJob.jobId]);
    useEffect(function () {
        if (isFailed)
            showError(errorMessageId);
    }, [isFailed, showError, errorMessageId]);
    var _f = useMutation(function (data) { return __awaiter(void 0, void 0, void 0, function () {
        var response;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, generateReport(ky, data)];
                case 1:
                    response = _a.sent();
                    if (!isReportJobStarted(response)) {
                        throw new Error("Report generation answered without a job id");
                    }
                    reportJob.start(response);
                    return [2 /*return*/, response];
            }
        });
    }); }, {
        onMutate: function () {
            setSuccessMessage("");
            resetJob();
        },
        onError: function (error) {
            var _a, _b;
            return showError(((_b = (_a = toErrorWithResponse(error)) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.status) ===
                HTTP_TOO_MANY_REQUESTS
                ? "ui-br-agency.outputs.report-job.error.queueFull"
                : errorMessageId);
        },
    }), mutate = _f.mutate, isLoading = _f.isLoading;
    var onDownload = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setIsDownloading(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, reportJob.download()];
                case 2:
                    _a.sent();
                    setSuccessMessage(formatMessage({ id: successMessageId }));
                    return [3 /*break*/, 5];
                case 3:
                    error_1 = _a.sent();
                    console.error("Error downloading report:", error_1);
                    showError("ui-br-agency.outputs.report-job.error.download");
                    return [3 /*break*/, 5];
                case 4:
                    setIsDownloading(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [reportJob, showError, formatMessage, successMessageId]);
    var jobView = {
        isGenerating: reportJob.isGenerating,
        isDone: isDone,
        isDownloading: isDownloading,
        onDownload: onDownload,
        statusUnknown: pollFailed,
        onRetryStatus: reportJob.retry,
    };
    return {
        mutate: mutate,
        isLoading: isLoading || reportJob.isGenerating,
        reportJob: jobView,
        successMessage: successMessage,
        clearSuccessMessage: clearSuccessMessage,
        errorModalOpen: errorModalOpen,
        errorMessage: errorMessage,
        closeErrorModal: closeErrorModal,
    };
};
